<script setup lang="ts">
import type { Blocks, BreadcrumbListItem, ProductDetailPageContent, Metadata } from 'types';

const props = defineProps<{
  blocks: Blocks;
  content: ProductDetailPageContent;
  breadcrumbs: BreadcrumbListItem[];
  metadata: Metadata;
}>();

const root = useRoot();
const culture = root.settings.metadata.culture;
const productQuantity = ref<number>(1);
const isContactFormShown = ref<boolean>(false);
const slideIn = ref<HTMLElement>();
const layout = useLayout();

const blocks = computed(() => {
  return props.blocks.blocks || {};
});

const calculatedPrice = computed(() => {
  return props.content.productPrice - props.content.productDiscount;
});

const formattedDate = computed(() => {
  return new Date(props.content.endOfDiscount).toLocaleDateString(culture, {
    year: 'numeric',
    month: '2-digit',
    day: 'numeric',
  });
});

const relatedProductsSliderOptions = {
  arrows: false,
  perPage: 1,
  breakpoints: {
    1536: {
      perPage: 5,
    },
  },
};

function incrementQuantity() {
  productQuantity.value += 1;
}

function decrementQuantity() {
  productQuantity.value -= 1;
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  productQuantity.value = Number(target.value);
}

function toggleContactForm() {
  isContactFormShown.value = !isContactFormShown.value;
}
</script>

<template>
  <section
    v-if="content"
    class="flex flex-col"
  >
    <section
      class="container relative flex flex-col overflow-clip py-20 lg:flex-row lg:items-center lg:justify-between lg:gap-5 xl:gap-20"
      :class="{
        'xl:gap-40': content.productPrice || content.productDiscount || content.endOfDiscount,
      }"
    >
      <PartialsThumbnailSlider :content="content?.gallery" />

      <div
        v-if="!content.productPrice || !content.productDiscount || !content.endOfDiscount"
        class="flex flex-row lg:max-w-[30%]"
      >
        <div class="flex flex-col gap-y-8">
          <h1 class="w-fit font-semibold xl:text-h2">{{ content?.title }}</h1>
          <p>{{ content?.productNumber }}</p>
          <span class="max-w-full text-sm lg:text-base">
            <BaseRte
              v-if="content?.longDescription"
              :content="content?.longDescription"
            />
          </span>
          <BaseButton
            v-if="content?.contactForm"
            class="w-fit"
            :size="layout.isMobile ? 'small' : 'large'"
            @click="toggleContactForm"
          >
            {{ $t('ProductDetailPage.ContactToOrder') }}
          </BaseButton>
        </div>

        <BaseSlideIn
          v-if="content?.contactForm"
          ref="slideIn"
          :is-shown="isContactFormShown"
          @close-slide-in="isContactFormShown = false"
        >
          <div class="size-full">
            <h2 class="mb-5 text-h1 xl:text-h2">
              {{ $t('FormMessages.General.ContactUs') }}
            </h2>
            <p class="mb-10">
              {{ $t('FormMessages.General.ContactUs.SubHeader') }}
            </p>

            <BaseForm :form-data="content?.contactForm" />
          </div>
        </BaseSlideIn>
      </div>

      <div
        v-else
        class="flex flex-col gap-y-12 lg:max-w-1/2"
      >
        <div class="flex flex-col gap-y-2">
          <h1 class="font-semibold md:text-h2">{{ content?.title }}</h1>
          <span class="text-base text-black/70 line-through">
            {{ content?.productPrice }} {{ $t('ProductDetailPage.ProductPriceValuta') }}
          </span>

          <span class="text-h3 font-semibold">
            {{ calculatedPrice }} {{ $t('ProductDetailPage.ProductPriceValuta') }}
          </span>
          <span class="text-black/70">{{ $t('ProductDetailPage.ProductEndOfDiscount') }} {{ formattedDate }}</span>
        </div>

        <div class="flex gap-x-4">
          <div class="flex items-center">
            <BaseButton
              :aria-label="$t('Aria.ProductDetailPage.DecreaseQuantity')"
              :arrow-icon="false"
              intent="quantitySelectorDecrease"
              size="quantitySelector"
              @click="decrementQuantity"
            >
              <BaseIcon
                name="chevron-down"
                prefix="arrows"
              />
            </BaseButton>

            <input
              id="quantity-input"
              ref="quantity"
              v-model="productQuantity"
              :aria-label="$t('Aria.ProductDetailPage.ProductQuantity', { quantity: productQuantity })"
              type="number"
              class="h-full w-16 border-y-2 border-gray-90 p-2 text-center text-h4"
              inputmode="text"
              @input="handleInput"
            />

            <BaseButton
              :aria-label="$t('Aria.ProductDetailPage.IncreaseQuantity')"
              :arrow-icon="false"
              intent="quantitySelectorIncrease"
              size="quantitySelector"
              @click="incrementQuantity"
            >
              <BaseIcon
                prefix="arrows"
                name="chevron-down"
                class="rotate-180"
              />
            </BaseButton>
          </div>

          <BaseButton
            :arrow-icon="false"
            size="medium"
            class="text-nowrap"
          >
            {{ $t('ProductDetailPage.ProductAddToBasket') }}
          </BaseButton>
        </div>
      </div>
    </section>

    <BlockList
      v-if="blocks"
      :blocks="blocks"
    />

    <section
      v-if="content?.relatedProducts"
      class="container py-4 pb-20 lg:gap-40"
    >
      <BaseSlider
        :disable-progress="true"
        :slider-options="relatedProductsSliderOptions"
      >
        <BaseSliderItem
          v-for="product in content.relatedProducts"
          :key="product.title"
        >
          <BaseCard>
            <template #header>
              <BaseImage
                class="h-52 w-full object-cover"
                :image="product.media"
              />
              <h2>{{ product.title }}</h2>
            </template>

            <template #default>
              <p class="truncate text-base">{{ product.shortDescription }}</p>
            </template>

            <template #footer>
              <BaseButton :to="handleRelativeUrl(product.url)">
                {{ $t('ProductDetailPage.ViewProduct') }}
              </BaseButton>
            </template>
          </BaseCard>
        </BaseSliderItem>
      </BaseSlider>
    </section>
  </section>
</template>

<style lang="postcss" scoped>
#quantity-input[type='number'] {
  appearance: textfield;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
